import { Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';

// Only one preview plays at a time across every instance on the page: the
// active player is tracked at module level so starting a new one stops the
// previous, whichever component it lives in.
let activePlayer: VoicePreviewButtonComponent | null = null;

export type VoicePreviewState = 'idle' | 'loading' | 'playing';

// Round play/pause button that streams a short TTS sample (ENG-2292). Owns a
// plain HTMLAudioElement — no player library — and exposes a loading state
// while the stream buffers.
@Component({
  selector: 'rosie-voice-preview-button',
  templateUrl: './voice-preview-button.component.html',
  styleUrls: ['./voice-preview-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class VoicePreviewButtonComponent implements OnDestroy {
  // Full audio URL (e.g. the /tts stream). Changing it while playing stops
  // playback.
  @Input() set src(value: string) {
    if (value !== this._src) {
      this.stop();
    }
    this._src = value;
  }
  get src(): string {
    return this._src;
  }
  @Input() ariaLabel = 'Preview voice';

  state: VoicePreviewState = 'idle';
  private _src = '';
  private audio: HTMLAudioElement | null = null;

  get isPlaying(): boolean {
    return this.state === 'playing';
  }

  get isLoading(): boolean {
    return this.state === 'loading';
  }

  toggle(event?: Event): void {
    event?.stopPropagation();
    if (this.state !== 'idle') {
      this.stop();
      return;
    }
    this.play();
  }

  play(): void {
    if (!this.src) {
      return;
    }
    if (activePlayer && activePlayer !== this) {
      activePlayer.stop();
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- module-level "one player at a time" registry
    activePlayer = this;
    this.state = 'loading';
    const audio = new Audio(this.src);
    this.audio = audio;
    audio.addEventListener('playing', () => this.onPlaying(audio));
    audio.addEventListener('ended', () => this.onEnded(audio));
    audio.addEventListener('error', () => this.onEnded(audio));
    audio.play().catch(() => this.onEnded(audio));
  }

  stop(): void {
    if (this.audio) {
      const audio = this.audio;
      this.audio = null;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    if (activePlayer === this) {
      activePlayer = null;
    }
    this.state = 'idle';
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private onPlaying(audio: HTMLAudioElement): void {
    if (audio === this.audio) {
      this.state = 'playing';
    }
  }

  private onEnded(audio: HTMLAudioElement): void {
    if (audio === this.audio) {
      this.stop();
    }
  }
}
